import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FlaskConical, Plus, Trash2, Save, ExternalLink, Info } from "lucide-react";

function uid() { return Math.random().toString(36).slice(2, 10); }

const LINKS = [
  { label: "CASA — Drug & Alcohol Management Plans (DAMP)", url: "https://www.casa.gov.au/drug-and-alcohol-management-plans" },
  { label: "CASR Part 99 — Drug & alcohol management", url: "https://www.casa.gov.au/rules/regulatory-framework/casr/part-99-casr-drug-and-alcohol-management" },
];

export default function DampPage() {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/templates/damp").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);

  const update = (id, val) => setItems((it) => it.map((x) => (x.id === id ? { ...x, item: val } : x)));
  const patch = (id, p) => setItems((it) => it.map((x) => (x.id === id ? { ...x, ...p } : x)));
  const add = () => setItems((it) => [...it, { id: uid(), item: "", scope: "both" }]);
  const remove = (id) => setItems((it) => it.filter((x) => x.id !== id));

  const save = async () => {
    const clean = items.filter((x) => x.item.trim());
    setSaving(true);
    try {
      await api.put("/templates/damp", { items: clean });
      setItems(clean);
      toast.success("DAMP template saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">CASR Part 99 · Template</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading flex items-center gap-2">
            <FlaskConical className="h-8 w-8 text-lime-500" /> DAMP
          </h1>
          <p className="text-slate-500 mt-1">Manage the DAMP checklist template. Pulled into every job for a per-job DAMP check.</p>
        </div>
        <Button data-testid="damp-save-btn" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save Template"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 flex gap-3">
            <Info className="h-5 w-5 text-sky-600 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700">Edit the checklist items below. Open any job → <strong>DAMP Check</strong> to tick off items for that specific job and record completion.</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-2">
            {items.map((it, i) => (
              <div key={it.id} data-testid={`damp-item-${i}`} className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-400 w-6 shrink-0">{i + 1}</span>
                <Input value={it.item} onChange={(e) => update(it.id, e.target.value)} placeholder="Checklist item" className="flex-1" />
                <select value={it.scope || "both"} onChange={(e) => patch(it.id, { scope: e.target.value })} data-testid={`damp-scope-${i}`}
                  className="h-9 rounded-md border border-slate-200 px-2 text-xs shrink-0 bg-white" title="Where this item applies">
                  <option value="both">Job + Company</option>
                  <option value="job">Job only</option>
                  <option value="company">Company only</option>
                </select>
                <Button size="icon" variant="outline" data-testid={`damp-remove-${i}`} onClick={() => remove(it.id)} className="text-red-600 border-red-200 hover:bg-red-50 shrink-0"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" onClick={add} data-testid="damp-add-btn" className="w-full border-dashed mt-2"><Plus className="h-4 w-4 mr-1.5" /> Add Item</Button>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold font-heading text-slate-900 mb-4">Resources</h2>
            <div className="space-y-3">
              {LINKS.map((l) => (
                <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" data-testid="damp-link"
                  className="flex items-center justify-between gap-2 p-3 rounded-lg border border-slate-200 hover:border-lime-300 hover:bg-lime-50/50 transition-all text-sm text-slate-700">
                  <span>{l.label}</span><ExternalLink className="h-4 w-4 text-slate-400 shrink-0" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Right: what DAMP is & how to use this page */}
        <aside className="bg-white border border-slate-200 rounded-xl p-6 lg:sticky lg:top-24 space-y-5" data-testid="damp-about">
          <div>
            <h2 className="text-lg font-semibold font-heading text-slate-900 flex items-center gap-2"><FlaskConical className="h-5 w-5 text-lime-500" /> What is a DAMP?</h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              A <strong>Drug &amp; Alcohol Management Plan (DAMP)</strong> is required under <strong>CASR Part 99</strong> for organisations performing safety-sensitive aviation activities (SSAA) — which includes commercial drone (RPA) operations. It sets out how you prevent, detect and respond to drug and alcohol use that could affect the safety of a flight.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-1.5">A DAMP typically covers</h3>
            <ul className="text-sm text-slate-600 space-y-1.5">
              <li className="flex gap-2"><span className="text-lime-600 font-bold">•</span> Education &amp; awareness for all crew</li>
              <li className="flex gap-2"><span className="text-lime-600 font-bold">•</span> Testing (pre-employment, random, for-cause, post-incident)</li>
              <li className="flex gap-2"><span className="text-lime-600 font-bold">•</span> Response, stand-down &amp; return-to-work procedures</li>
              <li className="flex gap-2"><span className="text-lime-600 font-bold">•</span> Record keeping &amp; responsibilities</li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-1.5">How to use this page</h3>
            <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside">
              <li>Edit the checklist template on the left to match your DAMP.</li>
              <li>Click <strong>Save Template</strong> to apply it across all jobs.</li>
              <li>Open any job → <strong>DAMP Check</strong> to tick items off and record completion for that specific flight.</li>
            </ol>
          </div>
          <p className="text-xs text-slate-400 border-t border-slate-100 pt-3">This checklist is an operational aid — it does not replace your full CASR Part 99 DAMP document. Always confirm current requirements on casa.gov.au.</p>
        </aside>
      </div>
    </div>
  );
}
